import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { MdClose } from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';

export interface ConfirmationModalProps {
  /**
   * Unique id to identify this modal
   */
  id: string;
  /**
   * Open/close state of the modal
   */
  open: boolean;
  /**
   * Dialog title bar text
   */
  title: string;
  /**
   * Action to perform on "No" click. Omit for acknowledge-only modals —
   * the "No" button is not rendered.
   */
  onCancel?: () => void;
  /**
   * Action to perform on "Yes" click
   */
  onConfirm: () => void;
  /**
   * Text to display on the "No" button. Defaults to "No"
   */
  cancelText?: string;
  /**
   * Text to display on the "Yes" button. Defaults to "Yes"
   */
  confirmText?: string;
  /**
   * Variant of the "Yes" button
   */
  confirmButtonVariant?: ButtonProps['variant'];
  /**
   * Text to display in the body of the modal
   */
  message?: string;
  /**
   * Children to display in the modal body; only shown if `message` is not provided
   */
  children?: React.ReactNode;
}

/**
 * A modal with sensible defaults for a confirmation popup
 */
export function ConfirmationModal({
  id,
  open,
  title,
  onCancel,
  onConfirm,
  cancelText,
  confirmText,
  confirmButtonVariant = 'contained',
  message,
  children,
}: ConfirmationModalProps) {
  const { t } = useTranslation();
  const defaultCancelText = cancelText || t('no');
  const defaultConfirmText = confirmText || t('yes');
  const content = message ? (
    <Typography className="text-sm">{message}</Typography>
  ) : (
    children
  );
  return (
    <Dialog key={id} onClose={onCancel} open={open} maxWidth="md">
      <DialogTitle className="flex items-center justify-between p-6 gap-8">
        {title}
        {onCancel && (
          <IconButton onClick={onCancel} className="">
            <MdClose size={24} />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>{content}</DialogContent>
      <DialogActions>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel}>
            {defaultCancelText}
          </Button>
        )}
        <Button variant={confirmButtonVariant} onClick={onConfirm}>
          {defaultConfirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
